import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// ─── Constants ──────────────────────────────────────────────────────────────

const HEALTH_CHECK_INTERVAL = 30_000; // 30s when online
const HEALTH_CHECK_RETRY = 5_000; // 5s when offline (faster retry)
const HEALTH_ENDPOINT = '/health';

// ─── State ──────────────────────────────────────────────────────────────────

interface NetworkState {
  isOnline: boolean;
  isServerReachable: boolean;
  isConnected: boolean;
}

// ─── Singleton store — one health check timer for the whole app ─────────────

let timerHandle: ReturnType<typeof setTimeout> | null = null;
let initialized = false;

async function checkServer(set: (s: Partial<NetworkState>) => void) {
  if (!navigator.onLine) {
    set({ isServerReachable: false, isConnected: false });
    return;
  }
  try {
    const res = await fetch(HEALTH_ENDPOINT, {
      method: 'GET',
      cache: 'no-store',
      signal: AbortSignal.timeout(5000),
    });
    const reachable = res.ok;
    set({ isServerReachable: reachable, isConnected: navigator.onLine && reachable });
  } catch {
    set({ isServerReachable: false, isConnected: false });
  }
}

function scheduleCheck(set: (s: Partial<NetworkState>) => void) {
  if (timerHandle) clearTimeout(timerHandle);
  const interval = navigator.onLine ? HEALTH_CHECK_INTERVAL : HEALTH_CHECK_RETRY;
  timerHandle = setTimeout(async () => {
    await checkServer(set);
    scheduleCheck(set);
  }, interval);
}

export const useNetworkStore = create<NetworkState>()(devtools((set) => {
  if (!initialized) {
    initialized = true;

    const handleOnline = () => {
      set({ isOnline: true });
      checkServer(set);
      scheduleCheck(set);
    };

    const handleOffline = () => {
      set({ isOnline: false, isServerReachable: false, isConnected: false });
      scheduleCheck(set);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    checkServer(set);
    scheduleCheck(set);
  }

  return {
    isOnline: navigator.onLine,
    isServerReachable: navigator.onLine,
    isConnected: navigator.onLine,
  };
}, { name: 'network-store' }));
